<template>
  <div
    v-if="hasFocalPoint"
    :class="['overflow-hidden', containerClass]"
  >
    <img
      :src="src"
      :srcset="srcset || undefined"
      :sizes="sizes || undefined"
      :alt="computedAlt"
      :width="width"
      :height="height"
      :class="['object-cover', imgClass]"
      :style="styles()"
      :loading="loading"
      :fetchpriority="fetchpriority || undefined"
    >
  </div>
  <img
    v-else
    :src="src"
    :srcset="srcset || undefined"
    :sizes="sizes || undefined"
    :alt="computedAlt"
    :width="width"
    :height="height"
    :class="['object-cover', 'object-center', imgClass]"
    :style="styles()"
    :loading="loading"
    :fetchpriority="fetchpriority || undefined"
  >
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ImageWithFocalPoint } from '@/ts/types/focal-point';
import { getFocalPointStyle } from '@/ts/utils/focal-point';

interface Props {
  /** Image data with optional focal point information */
  image: ImageWithFocalPoint | { url: string; title?: string; alt?: string };
  /** Additional CSS classes for the img element */
  imgClass?: string;
  /** Additional inline styles for the img element */
  imgStyle?: string;
  /** Additional CSS classes for the container div (when focal point exists) */
  containerClass?: string;
  /** Override alt text */
  alt?: string;
  /** Image loading strategy */
  loading?: 'lazy' | 'eager';
  /** Fetch priority hint for the browser */
  fetchpriority?: 'high' | 'low' | 'auto';
  /** srcset attribute for responsive images */
  srcset?: string;
  /** sizes attribute for responsive images */
  sizes?: string;
}

const props = withDefaults(defineProps<Props>(), {
  imgClass: '',
  imgStyle: '',
  containerClass: '',
  loading: 'lazy',
});

/** Check if the image has focal point data */
const hasFocalPoint = computed(() => {
  return 'focal_point' in props.image && props.image.focal_point !== null;
});

/** Get image source URL */
const src = computed(() => {
  return props.image.url;
});

/** Get image width */
const width = computed(() => {
  return 'width' in props.image ? props.image.width : undefined;
});

/** Get image height */
const height = computed(() => {
  return 'height' in props.image ? props.image.height : undefined;
});

/** Compute alt text from props or image data */
const computedAlt = computed(() => {
  if (props.alt) return props.alt;
  if ('alt' in props.image && props.image.alt) return props.image.alt;
  if ('title' in props.image && props.image.title) return props.image.title;
  return '';
});

/** Generate CSS style for focal point or default centering */
const focalStyle = computed(() => {
  if (!hasFocalPoint.value) {
    return { objectPosition: 'center' };
  }

  const img = props.image as ImageWithFocalPoint;
  return getFocalPointStyle(
    img.width,
    img.height,
    img.focal_point!
  );
});

const styles = () => {
  return `${props.imgStyle} ${focalStyle.value}`
}
</script>
